/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { DefaultButton } from '@fluentui/react/lib/Button';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { Switch, useHistory } from 'react-router';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDTButtonClick, logRedMessageBarVisible } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { getDTActiveStepName } from 'src/core/utils/telemetryUtils';
import { SignedOutEmptyCustomization } from 'src/languageStudio/appShell/SignedOutEmptyCustomization';
import { ResourceValidationResult, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';
import { TranslationBreadcrumb } from 'src/translation/components/TranslationBreadcrumb';
import { TranslationCreationPage, TranslationCreationSteps } from 'src/translation/components/TranslationCreationPage';
import { TranslationHistoryPage } from 'src/translation/components/TranslationHistoryPage';
import { TranslationSettingsPage } from 'src/translation/components/TranslationSettingsPage';
import { TranslationSidebar } from 'src/translation/components/TranslationSidebar';
import { getTranslationPageName } from 'src/translation/utils/translationCreationUtils';

const { styleDiv, styleComponent } = getStylist('TranslationRoutes');
const TranslationContentContainer = styleComponent<IStackProps>(Stack)('TranslationContentContainer', { overflow: 'hidden' });
const BreadcrumbWrapper = styleDiv('CluBreadcrumbWrapper', { paddingLeft: 24 });

const messages = defineMessages({
    goToSettings: { id: 'translation.translationRoutes.goToSettings', defaultMessage: 'Go to Settings' }
});

export const TranslationRoutes = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const { csAccountStore } = rootStore.azureStore.session;
    const history = useHistory();

    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: true });

    React.useEffect(() => {
        if (csAccountStore?.id) {
            setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
        }
    }, [csAccountStore?.id]);

    const pathName = history.location.pathname;

    const pageName = React.useMemo(() => getTranslationPageName(pathName), [pathName]);

    React.useEffect(() => {
        if (!isValidResource.isValid) {
            logRedMessageBarVisible(
                'document translation',
                pageName,
                pageName === 'jobCreation' ? getDTActiveStepName(TranslationCreationSteps.InitConfiguration) : null
            );
        }
    }, [isValidResource.isValid, pageName]);

    const handleGoToSettingClick = () => {
        dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true });
        logDTButtonClick(
            'document translation',
            pageName,
            'Button',
            'openTranslationModal',
            pageName === 'jobCreation' ? getDTActiveStepName(TranslationCreationSteps.InitConfiguration) : null
        );
    };

    return (
        <TranslationContentContainer horizontal grow>
            <TranslationSidebar />
            <TranslationContentContainer grow>
                <BreadcrumbWrapper>
                    <TranslationBreadcrumb />
                </BreadcrumbWrapper>

                {!isValidResource.isValid && (
                    <MessageBar messageBarType={MessageBarType.blocked} styles={{ innerText: { width: '100%' } }}>
                        <Stack tokens={{ childrenGap: 5 }}>
                            {isValidResource.mainErrorMessage}
                            <DefaultButton styles={{ root: { alignSelf: 'flex-end' } }} onClick={handleGoToSettingClick}>
                                {localize(messages.goToSettings)}
                            </DefaultButton>
                        </Stack>
                    </MessageBar>
                )}

                {!isSignedIn ? (
                    <SignedOutEmptyCustomization
                        service="customTranslation"
                        serviceDocumentationLink="https://aka.ms/Document-translation-studio"
                    />
                ) : (
                    <Switch>
                        <PrivateRoute exact path={routeTable.translation.root()} component={TranslationCreationPage} />
                        <PrivateRoute
                            exact
                            path={routeTable.translation.documentTranslationSettings()}
                            component={TranslationSettingsPage}
                        />
                        <PrivateRoute exact path={routeTable.translation.documentTranslationHistory()} component={TranslationHistoryPage} />
                        <NotFoundRedirect />
                    </Switch>
                )}
            </TranslationContentContainer>
        </TranslationContentContainer>
    );
});
